/**
 * Minimal skeleton-style SVG illustrations for loyalty program guide slides.
 *
 * Each illustration is ~280×100, uses currentColor + opacity for theme-
 * adaptive rendering. Intentionally simple — communicates the concept
 * without being a full graphic.
 */

const W = 280;
const H = 100;

/* ── Shared palette (opacity-based so they adapt to light/dark) ── */

const muted = "currentColor";
const op = (o: number) => String(o); // shorthand for opacity

/* ── Program Name ── */

/** Storefront badge with a program name label */
export function IllustNameBadge() {
  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} fill="none" className="text-muted-foreground">
      {/* Card */}
      <rect x="60" y="12" width="160" height="76" rx="12" stroke={muted} strokeWidth="1.5" opacity={op(0.2)} />
      {/* Star icon */}
      <circle cx="100" cy="42" r="10" fill={muted} opacity={op(0.08)} />
      <path d="M100 35l2 5h5l-4 3 1.5 5-4.5-3-4.5 3 1.5-5-4-3h5z" fill={muted} opacity={op(0.25)} />
      {/* Name skeleton lines */}
      <rect x="118" y="38" width="80" height="8" rx="4" fill={muted} opacity={op(0.15)} />
      {/* Subtitle skeleton */}
      <rect x="118" y="52" width="56" height="6" rx="3" fill={muted} opacity={op(0.08)} />
      {/* "Rewards" badge pill */}
      <rect x="90" y="68" width="100" height="14" rx="7" fill={muted} opacity={op(0.06)} />
      <rect x="104" y="72" width="72" height="6" rx="3" fill={muted} opacity={op(0.12)} />
    </svg>
  );
}

/** Pencil editing a name field */
export function IllustNameEdit() {
  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} fill="none" className="text-muted-foreground">
      {/* Text field */}
      <rect x="50" y="30" width="150" height="40" rx="8" stroke={muted} strokeWidth="1.5" opacity={op(0.2)} />
      {/* Text skeleton inside field */}
      <rect x="64" y="44" width="90" height="8" rx="4" fill={muted} opacity={op(0.15)} />
      {/* Cursor blink line */}
      <rect x="160" y="40" width="2" height="20" rx="1" fill={muted} opacity={op(0.3)} />
      {/* Pencil icon */}
      <g transform="translate(212, 32)" opacity="0.3">
        <path d="M0 28l2-8L22 0l6 6L8 26z" stroke={muted} strokeWidth="1.5" fill={muted} fillOpacity="0.05" />
        <path d="M0 28l2-8" stroke={muted} strokeWidth="1.5" />
      </g>
    </svg>
  );
}

/* ── Expiry ── */

/** Timeline with points fading at the end */
export function IllustExpiryTimeline() {
  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} fill="none" className="text-muted-foreground">
      {/* Timeline line */}
      <line x1="40" y1="50" x2="240" y2="50" stroke={muted} strokeWidth="1.5" opacity={op(0.12)} />
      {/* Point markers — solid to fading */}
      <circle cx="60" cy="50" r="8" fill={muted} opacity={op(0.25)} />
      <circle cx="110" cy="50" r="8" fill={muted} opacity={op(0.2)} />
      <circle cx="160" cy="50" r="8" fill={muted} opacity={op(0.12)} />
      <circle cx="210" cy="50" r="8" fill={muted} opacity={op(0.05)} />
      {/* Point labels */}
      <text x="60" y="48" textAnchor="middle" dominantBaseline="middle" fontSize="8" fontWeight="600" fill={muted} opacity={op(0.4)}>P</text>
      <text x="110" y="48" textAnchor="middle" dominantBaseline="middle" fontSize="8" fontWeight="600" fill={muted} opacity={op(0.3)}>P</text>
      <text x="160" y="48" textAnchor="middle" dominantBaseline="middle" fontSize="8" fontWeight="600" fill={muted} opacity={op(0.18)}>P</text>
      <text x="210" y="48" textAnchor="middle" dominantBaseline="middle" fontSize="8" fontWeight="600" fill={muted} opacity={op(0.08)}>P</text>
      {/* Day labels */}
      <text x="60" y="72" textAnchor="middle" fontSize="7" fill={muted} opacity={op(0.2)}>Day 1</text>
      <text x="210" y="72" textAnchor="middle" fontSize="7" fill={muted} opacity={op(0.2)}>Day 365</text>
      {/* Expiry X mark */}
      <line x1="205" y1="34" x2="215" y2="44" stroke={muted} strokeWidth="1.5" opacity={op(0.2)} />
      <line x1="215" y1="34" x2="205" y2="44" stroke={muted} strokeWidth="1.5" opacity={op(0.2)} />
    </svg>
  );
}

/** Clock with a circular urgency indicator */
export function IllustExpiryUrgency() {
  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} fill="none" className="text-muted-foreground">
      {/* Clock face */}
      <circle cx="110" cy="50" r="28" stroke={muted} strokeWidth="1.5" opacity={op(0.15)} />
      <circle cx="110" cy="50" r="2" fill={muted} opacity={op(0.3)} />
      {/* Clock hands */}
      <line x1="110" y1="50" x2="110" y2="30" stroke={muted} strokeWidth="1.5" opacity={op(0.3)} />
      <line x1="110" y1="50" x2="124" y2="54" stroke={muted} strokeWidth="1.5" opacity={op(0.2)} />
      {/* Arrow curving back (customer returns) */}
      <path d="M155 40 C175 30, 195 30, 195 50 C195 70, 175 70, 155 60" stroke={muted} strokeWidth="1.5" opacity={op(0.2)} fill="none" />
      <path d="M155 40l6 2-2 6" stroke={muted} strokeWidth="1.5" opacity={op(0.2)} />
      {/* Shop icon simplified */}
      <rect x="175" y="42" width="16" height="14" rx="2" stroke={muted} strokeWidth="1" opacity={op(0.15)} />
      <path d="M175 46h16" stroke={muted} strokeWidth="1" opacity={op(0.15)} />
    </svg>
  );
}

/* ── Conversion Factor ── */

/** Points coin → arrow → baht symbol */
export function IllustConversionRate() {
  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} fill="none" className="text-muted-foreground">
      {/* Points coin */}
      <circle cx="80" cy="50" r="24" stroke={muted} strokeWidth="1.5" opacity={op(0.15)} />
      <circle cx="80" cy="50" r="16" stroke={muted} strokeWidth="1" opacity={op(0.08)} />
      <text x="80" y="48" textAnchor="middle" dominantBaseline="middle" fontSize="14" fontWeight="700" fill={muted} opacity={op(0.25)}>1</text>
      <text x="80" y="60" textAnchor="middle" fontSize="6" fill={muted} opacity={op(0.15)}>PT</text>
      {/* Arrow */}
      <line x1="115" y1="50" x2="155" y2="50" stroke={muted} strokeWidth="1.5" opacity={op(0.2)} />
      <path d="M150 45l8 5-8 5" stroke={muted} strokeWidth="1.5" opacity={op(0.2)} fill="none" />
      {/* = sign */}
      <rect x="130" y="46" width="10" height="2" rx="1" fill={muted} opacity={op(0.15)} />
      <rect x="130" y="52" width="10" height="2" rx="1" fill={muted} opacity={op(0.15)} />
      {/* Baht value */}
      <circle cx="190" cy="50" r="24" stroke={muted} strokeWidth="1.5" opacity={op(0.15)} />
      <text x="190" y="52" textAnchor="middle" dominantBaseline="middle" fontSize="18" fontWeight="600" fill={muted} opacity={op(0.25)}>&#xe3f;</text>
    </svg>
  );
}

/** Coin with a pause symbol — redemption disabled */
export function IllustConversionOff() {
  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} fill="none" className="text-muted-foreground">
      {/* Points coin */}
      <circle cx="140" cy="50" r="28" stroke={muted} strokeWidth="1.5" opacity={op(0.12)} />
      <text x="140" y="46" textAnchor="middle" dominantBaseline="middle" fontSize="14" fontWeight="600" fill={muted} opacity={op(0.15)}>0</text>
      <text x="140" y="60" textAnchor="middle" fontSize="7" fill={muted} opacity={op(0.1)}>= off</text>
      {/* Pause bars */}
      <rect x="126" y="36" width="4" height="16" rx="2" fill={muted} opacity={op(0.2)} />
      <rect x="134" y="36" width="4" height="16" rx="2" fill={muted} opacity={op(0.2)} />
      {/* Strikethrough diagonal */}
      <line x1="118" y1="72" x2="162" y2="28" stroke={muted} strokeWidth="1.5" opacity={op(0.15)} />
    </svg>
  );
}

/* ── Auto Opt-In ── */

/** Multiple person outlines with checkmarks */
export function IllustAutoJoin() {
  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} fill="none" className="text-muted-foreground">
      {/* Person 1 */}
      <circle cx="90" cy="32" r="10" stroke={muted} strokeWidth="1.5" opacity={op(0.15)} />
      <path d="M74 68a16 16 0 0132 0" stroke={muted} strokeWidth="1.5" opacity={op(0.15)} />
      {/* Checkmark on person 1 */}
      <path d="M104 28l3 4 6-7" stroke={muted} strokeWidth="1.5" opacity={op(0.3)} />
      {/* Person 2 */}
      <circle cx="150" cy="32" r="10" stroke={muted} strokeWidth="1.5" opacity={op(0.15)} />
      <path d="M134 68a16 16 0 0132 0" stroke={muted} strokeWidth="1.5" opacity={op(0.15)} />
      {/* Checkmark on person 2 */}
      <path d="M164 28l3 4 6-7" stroke={muted} strokeWidth="1.5" opacity={op(0.3)} />
      {/* Person 3 */}
      <circle cx="210" cy="32" r="10" stroke={muted} strokeWidth="1.5" opacity={op(0.12)} />
      <path d="M194 68a16 16 0 0132 0" stroke={muted} strokeWidth="1.5" opacity={op(0.12)} />
      {/* Checkmark animating on person 3 */}
      <path d="M224 28l3 4 6-7" stroke={muted} strokeWidth="1.5" opacity={op(0.18)} strokeDasharray="14" strokeDashoffset="0" />
    </svg>
  );
}

/** Person with a signup form — manual opt-in */
export function IllustManualJoin() {
  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} fill="none" className="text-muted-foreground">
      {/* Person */}
      <circle cx="90" cy="35" r="12" stroke={muted} strokeWidth="1.5" opacity={op(0.15)} />
      <path d="M72 76a18 18 0 0136 0" stroke={muted} strokeWidth="1.5" opacity={op(0.15)} />
      {/* Arrow pointing to form */}
      <line x1="120" y1="50" x2="148" y2="50" stroke={muted} strokeWidth="1.5" opacity={op(0.15)} />
      <path d="M144 46l6 4-6 4" stroke={muted} strokeWidth="1.5" opacity={op(0.15)} fill="none" />
      {/* Signup form */}
      <rect x="155" y="20" width="70" height="60" rx="8" stroke={muted} strokeWidth="1.5" opacity={op(0.15)} />
      {/* Form field lines */}
      <rect x="165" y="34" width="50" height="6" rx="3" fill={muted} opacity={op(0.08)} />
      <rect x="165" y="46" width="50" height="6" rx="3" fill={muted} opacity={op(0.08)} />
      {/* Submit button */}
      <rect x="175" y="60" width="30" height="10" rx="5" fill={muted} opacity={op(0.12)} />
    </svg>
  );
}

/* ── Tiers ── */

/** 3 stacked tier bars with labels */
export function IllustTierLevels() {
  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} fill="none" className="text-muted-foreground">
      {/* Tier 3 (top — widest) */}
      <rect x="60" y="14" width="160" height="18" rx="9" fill={muted} opacity={op(0.06)} />
      <circle cx="76" cy="23" r="5" fill={muted} opacity={op(0.12)} />
      <rect x="88" y="20" width="40" height="6" rx="3" fill={muted} opacity={op(0.1)} />
      {/* Tier 2 (middle) */}
      <rect x="80" y="40" width="120" height="18" rx="9" fill={muted} opacity={op(0.1)} />
      <circle cx="96" cy="49" r="5" fill={muted} opacity={op(0.18)} />
      <rect x="108" y="46" width="36" height="6" rx="3" fill={muted} opacity={op(0.15)} />
      {/* Tier 1 (bottom — narrowest, most opaque) */}
      <rect x="100" y="66" width="80" height="18" rx="9" fill={muted} opacity={op(0.16)} />
      <circle cx="116" cy="75" r="5" fill={muted} opacity={op(0.25)} />
      <rect x="128" y="72" width="32" height="6" rx="3" fill={muted} opacity={op(0.2)} />
      {/* Upward arrow */}
      <path d="M48 78l0-56" stroke={muted} strokeWidth="1" opacity={op(0.1)} />
      <path d="M44 28l4-6 4 6" stroke={muted} strokeWidth="1" opacity={op(0.1)} fill="none" />
    </svg>
  );
}

/** Tier bars with multiplier rates */
export function IllustTierRates() {
  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} fill="none" className="text-muted-foreground">
      {/* Bar chart — 3 bars ascending */}
      <rect x="70" y="58" width="40" height="26" rx="4" fill={muted} opacity={op(0.08)} />
      <rect x="120" y="40" width="40" height="44" rx="4" fill={muted} opacity={op(0.12)} />
      <rect x="170" y="22" width="40" height="62" rx="4" fill={muted} opacity={op(0.18)} />
      {/* Rate labels */}
      <text x="90" y="74" textAnchor="middle" fontSize="9" fontWeight="600" fill={muted} opacity={op(0.2)}>1x</text>
      <text x="140" y="56" textAnchor="middle" fontSize="9" fontWeight="600" fill={muted} opacity={op(0.25)}>2x</text>
      <text x="190" y="38" textAnchor="middle" fontSize="9" fontWeight="600" fill={muted} opacity={op(0.35)}>3x</text>
      {/* Base line */}
      <line x1="60" y1="86" x2="220" y2="86" stroke={muted} strokeWidth="1" opacity={op(0.1)} />
      {/* Tier names below */}
      <text x="90" y="96" textAnchor="middle" fontSize="7" fill={muted} opacity={op(0.15)}>Silver</text>
      <text x="140" y="96" textAnchor="middle" fontSize="7" fill={muted} opacity={op(0.15)}>Gold</text>
      <text x="190" y="96" textAnchor="middle" fontSize="7" fill={muted} opacity={op(0.15)}>Plat</text>
    </svg>
  );
}

/** Progress bar with milestone markers toward next tier */
export function IllustTierProgress() {
  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} fill="none" className="text-muted-foreground">
      {/* Track */}
      <rect x="40" y="44" width="200" height="12" rx="6" fill={muted} opacity={op(0.06)} />
      {/* Fill (60% progress) */}
      <rect x="40" y="44" width="120" height="12" rx="6" fill={muted} opacity={op(0.15)} />
      {/* Milestone markers */}
      <circle cx="40" cy="50" r="4" fill={muted} opacity={op(0.2)} />
      <circle cx="140" cy="50" r="4" fill={muted} opacity={op(0.2)} />
      <circle cx="240" cy="50" r="4" stroke={muted} strokeWidth="1.5" opacity={op(0.15)} />
      {/* Labels */}
      <text x="40" y="72" textAnchor="middle" fontSize="7" fill={muted} opacity={op(0.2)}>0</text>
      <text x="140" y="72" textAnchor="middle" fontSize="7" fill={muted} opacity={op(0.2)}>5,000</text>
      <text x="240" y="72" textAnchor="middle" fontSize="7" fill={muted} opacity={op(0.2)}>15,000</text>
      {/* Current position indicator */}
      <circle cx="160" cy="50" r="6" fill={muted} opacity={op(0.25)} />
      <path d="M157 50l2 2 4-4" stroke="white" strokeWidth="1.5" opacity={op(0.6)} />
      {/* Label above current */}
      <text x="160" y="34" textAnchor="middle" fontSize="7" fontWeight="600" fill={muted} opacity={op(0.25)}>฿7,200</text>
    </svg>
  );
}
